import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  Card,
  CardContent,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Collapse,
  IconButton,
  Chip,
  LinearProgress,
  Snackbar
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { apiClient as api } from '../../api';

interface Question {
  id: number;
  question_key: string;
  question_text: string;
  help_text?: string;
  answer_type: 'BOOLEAN' | 'SINGLE_SELECT' | 'MULTI_SELECT' | 'TEXT';
  answer_options?: string[];
  is_required: boolean;
}

interface ProcessScopingQuestionnaireProps {
  projectId: number;
  tenantId?: number;
  onComplete: (scopingResult: any) => void;
  onSkip?: () => void;
}

const ProcessScopingQuestionnaire: React.FC<ProcessScopingQuestionnaireProps> = ({
  projectId,
  tenantId,
  onComplete,
  onSkip
}) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);
  const [previewResult, setPreviewResult] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    loadQuestions();
    loadExistingSession();
  }, []);

  // Helper function to show notifications
  const showNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const loadQuestions = async () => {
    try {
      const response = await api.get('/api/risk-scoping/questions', {
        params: { tenant_id: tenantId }
      });
      setQuestions(response.data);
    } catch (err: any) {
      showNotification('Failed to load questions: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingSession = async () => {
    try {
      const response = await api.get(`/api/risk-scoping/sessions/${projectId}`);
      if (response.data && response.data.questionnaire_answers) {
        setAnswers(response.data.questionnaire_answers);
        setPreviewResult(response.data.scoping_results);
      }
    } catch (err: any) {
      // No existing session, that's fine
      console.log('No existing scoping session found');
    }
  };

  const updateAnswer = (questionKey: string, value: any) => {
    setAnswers({
      ...answers,
      [questionKey]: value
    });
    setShowPreview(false); // Reset preview when answers change
  };

  const validateAnswers = (): boolean => {
    const requiredQuestions = questions.filter(q => q.is_required);
    for (const q of requiredQuestions) {
      const answer = answers[q.question_key];
      // For boolean fields, check if answer is explicitly true or false (not undefined/null)
      if (q.answer_type === 'BOOLEAN') {
        if (answer !== true && answer !== false) {
          showNotification(`Please answer required question: ${q.question_text}`, 'warning');
          return false;
        }
      } else {
        // For other types, check if answer exists
        if (!answer) {
          showNotification(`Please answer required question: ${q.question_text}`, 'warning');
          return false;
        }
      }
    }
    return true;
  };

  const handlePreview = async () => {
    if (!validateAnswers()) {
      return;
    }

    setEvaluating(true);

    try {
      const response = await api.post('/api/risk-scoping/evaluate', {
        questionnaire_answers: answers,
        tenant_id: tenantId,
        project_id: null // Don't save yet, just preview
      });
      setPreviewResult(response.data);
      setShowPreview(true);
      showNotification('Preview generated successfully!', 'success');
    } catch (err: any) {
      showNotification('Failed to evaluate: ' + err.message, 'error');
    } finally {
      setEvaluating(false);
    }
  };

  const handleGenerate = async () => {
    console.log('🔍 DEBUG: handleGenerate called');
    console.log('🔍 DEBUG: answers:', answers);

    if (!validateAnswers()) {
      console.log('❌ Validation failed');
      return;
    }

    setEvaluating(true);

    try {
      console.log('📤 Sending request to /api/risk-scoping/evaluate');
      console.log('📦 Request payload:', {
        questionnaire_answers: answers,
        tenant_id: tenantId,
        project_id: projectId
      });

      const response = await api.post('/api/risk-scoping/evaluate', {
        questionnaire_answers: answers,
        tenant_id: tenantId,
        project_id: projectId // Save to session
      });

      console.log('✅ Response received:', response.data);
      console.log('🔍 applicableRiskIds in response:', response.data.applicableRiskIds);
      console.log('🎯 Calling onComplete with:', response.data);

      showNotification('Risk assessment generated successfully!', 'success');
      onComplete(response.data);
      console.log('✅ onComplete called successfully');
    } catch (err: any) {
      console.error('❌ Error in handleGenerate:', err);
      showNotification('Failed to generate risk assessment: ' + err.message, 'error');
    } finally {
      setEvaluating(false);
    }
  };

  const renderQuestionInput = (question: Question) => {
    switch (question.answer_type) {
      case 'BOOLEAN':
        const currentBoolValue = answers[question.question_key];
        const displayValue = currentBoolValue === true ? 'true' : currentBoolValue === false ? 'false' : '';

        return (
          <RadioGroup
            value={displayValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) => updateAnswer(question.question_key, e.target.value === 'true')}
            row
          >
            <FormControlLabel value="true" control={<Radio />} label="Yes" />
            <FormControlLabel value="false" control={<Radio />} label="No" />
          </RadioGroup>
        );

      case 'SINGLE_SELECT':
        return (
          <TextField
            select
            fullWidth
            size="small"
            value={answers[question.question_key] || ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => updateAnswer(question.question_key, e.target.value)}
          >
            {question.answer_options?.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        );

      case 'MULTI_SELECT':
        return (
          <FormGroup>
            {question.answer_options?.map((option) => (
              <FormControlLabel
                key={option}
                control={
                  <Checkbox
                    checked={answers[question.question_key]?.includes(option) || false}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const currentValues = answers[question.question_key] || [];
                      if (e.target.checked) {
                        updateAnswer(question.question_key, [...currentValues, option]);
                      } else {
                        updateAnswer(question.question_key, currentValues.filter((v: string) => v !== option));
                      }
                    }}
                  />
                }
                label={option}
              />
            ))}
          </FormGroup>
        );

      case 'TEXT':
        return (
          <TextField
            fullWidth
            size="small"
            value={answers[question.question_key] || ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => updateAnswer(question.question_key, e.target.value)}
            placeholder="Enter your answer..."
          />
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography>Loading questionnaire...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (questions.length === 0) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            No scoping questions configured. All risks will be assessed.
          </Alert>
          {onSkip && (
            <Button variant="contained" onClick={onSkip} sx={{ mt: 2 }}>
              Continue with All Risks
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={3}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h6">
            📋 Risk Scoping Questionnaire
          </Typography>
          <Chip label="NEW" color="primary" size="small" />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Answer these questions to identify applicable risks for your process.
          This will reduce assessment time by filtering out irrelevant risks.
        </Typography>

        {/* Questions */}
        <Box sx={{ mb: 3 }}>
          {questions.map((question, index) => (
            <Box key={question.id} sx={{ mb: 3 }}>
              <Typography variant="body1" gutterBottom>
                <strong>{index + 1}. {question.question_text}</strong>
                {question.is_required && (
                  <span style={{ color: 'red' }}> *</span>
                )}
              </Typography>

              {question.help_text && (
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <InfoIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {question.help_text}
                  </Typography>
                </Box>
              )}

              {renderQuestionInput(question)}
            </Box>
          ))}
        </Box>

        {/* Preview Results */}
        {showPreview && previewResult && (
          <Collapse in={showPreview}>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Preview Results
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={`${previewResult.statistics?.applicableRisks || 0} Applicable Risks`}
                  color="success"
                  size="small"
                />
                <Chip
                  label={`${previewResult.statistics?.excludedRisks || 0} Excluded (${previewResult.statistics?.reductionPercentage || 0}% reduction)`}
                  color="error"
                  size="small"
                />
                <Chip
                  label={`${previewResult.appliedRules?.length || 0} Rules Applied`}
                  color="primary"
                  size="small"
                />
              </Box>
            </Alert>
          </Collapse>
        )}

        {evaluating && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Evaluating scoping rules...
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          {onSkip && (
            <Button variant="outlined" onClick={onSkip}>
              Skip (Assess All Risks)
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={handlePreview}
            disabled={evaluating || Object.keys(answers).length === 0}
          >
            Preview Applicable Risks
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={evaluating || Object.keys(answers).length === 0}
            startIcon={<CheckIcon />}
          >
            Generate Risk Assessment
          </Button>
        </Box>
      </CardContent>

      {/* Toast Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default ProcessScopingQuestionnaire;
